import React from 'react';
import { useGate } from 'statsig-react';
import { twMerge } from 'tailwind-merge';
import { Divider } from '@components/main-components/divider';
import { RepostedByUsersModal } from '@components/modals/Post/RepostedBy';
import { UpvotedByUsersModal } from '@components/modals/Post/UpvotedByUsersPost';
import { useModal } from '@context/modal';
import {
  FeedCountSummaryFragmentDoc,
  type FragmentType,
  getFragment,
  gql,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { FEATURE_GATES } from '@utils/constants';
import { isRepostablePost } from './ActionButtons';
import { usePostStatus } from './usePostStatus';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedCountSummary on Post {
    __typename
    id
    repostCount
    commentCount
    upvoteCount
  }
`);

export function FeedCountSummary({
  post: postFrag,
}: {
  post: FragmentType<typeof FeedCountSummaryFragmentDoc>;
}) {
  const post = getFragment(FeedCountSummaryFragmentDoc, postFrag);
  const { repostCount, commentCount, upvoteCount, id: postId } = post;

  const { postStatus } = usePostStatus({
    postId,
  });

  const { openModal } = useModal();

  const { value: enabledReposts } = useGate(FEATURE_GATES.REPOSTS);

  if (!enabledReposts) return null;

  const upvoteCountNumber = postStatus?.upvoteCount ?? upvoteCount;

  const repostCountNumber = postStatus?.repostCount ?? repostCount;

  return (
    <>
      <div className="flex items-center">
        <div className="flex items-center gap-5 text-base-m">
          <div
            className={twMerge(
              'mr-1 flex items-center gap-1',
              !!upvoteCountNumber && 'cursor-pointer',
            )}
            onClick={() => {
              if (!upvoteCountNumber) return;

              trackClickEvent({
                event: 'Upvote Count Clicked',
                properties: {
                  associationId: postId,
                  associationType: 'Post',
                  /**
                   * This count is only shown on post page
                   */
                  sourceComponent: 'Post Page',
                  repostId: null,
                  repostUserId: null,
                  repostUserPublicAddress: null,
                },
              });

              openModal('REPOSTED_USERS', {
                body: <UpvotedByUsersModal postId={postId} />,
              });
            }}
          >
            <span className="font-semibold text-base800">{upvoteCountNumber}</span>
            <span className="font-medium text-base500">Upvotes</span>
          </div>
          {isRepostablePost(post) && (
            <div
              className={twMerge(
                'flex items-center gap-1',
                !!repostCountNumber && 'cursor-pointer',
              )}
              onClick={() => {
                if (!repostCountNumber) return;

                trackClickEvent({
                  event: 'Repost Count Clicked',
                  properties: {
                    associationId: postId,
                    associationType: 'Post',
                    /**
                     * This count is only shown on post page
                     */
                    sourceComponent: 'Post Page',
                    repostId: null,
                    repostUserId: null,
                    repostUserPublicAddress: null,
                  },
                });

                openModal('REPOSTED_USERS', {
                  body: <RepostedByUsersModal postId={postId} />,
                });
              }}
            >
              <span className="font-semibold text-base800">{repostCountNumber}</span>
              <span className="font-medium text-base500">Reposts</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="font-semibold text-base800">{commentCount}</span>
            <span className="font-medium text-base500">Replies</span>
          </div>
        </div>
      </div>
      <Divider className="m-0 bg-base300" />
    </>
  );
}
